/**
 * User-facing copy for Okta sign-in / token exchange failures (spec FR-003).
 */

const RETRY =
  "Try Sign in with Okta again. If the problem continues, contact your administrator or support.";

function parseJsonErrorBody(fragment: string): {
  error?: string;
  error_description?: string;
} | null {
  const trimmed = fragment.trim();
  if (!trimmed.startsWith("{")) {
    return null;
  }
  try {
    return JSON.parse(trimmed) as { error?: string; error_description?: string };
  } catch {
    return null;
  }
}

/**
 * Maps Okta `/v1/token` error payloads and thrown `Error` messages to short, actionable text.
 */
export function mapSignInFlowError(err: unknown): string {
  if (!(err instanceof Error)) {
    return `Sign-in did not finish. ${RETRY}`;
  }
  const raw = err.message;
  const tokenExchange = /^Token exchange failed \(\d+\):\s*(.*)$/s.exec(raw);
  const payload = tokenExchange ? tokenExchange[1] ?? "" : raw;
  const parsed = parseJsonErrorBody(payload);
  if (parsed?.error === "invalid_grant" || raw.includes("invalid_grant")) {
    return `Sign-in could not be completed. The sign-in step may have expired or already been used. ${RETRY}`;
  }
  if (parsed?.error === "invalid_client") {
    return `Sign-in could not be completed due to an application configuration issue. ${RETRY}`;
  }
  if (raw.startsWith("Token exchange failed")) {
    const devSuffix =
      import.meta.env.DEV && payload.length > 0
        ? ` Details: ${payload.length > 280 ? `${payload.slice(0, 280)}…` : payload}`
        : "";
    return `Sign-in did not finish. ${RETRY}${devSuffix}`;
  }
  return raw;
}
